import { useState, useEffect } from 'react';

/**
 * Hook to track a CSS media query match state.
 * @param query - CSS media query string (e.g., '(min-width: 768px)')
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
  const [prevQuery, setPrevQuery] = useState(query);
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  // Adjust state during render when the query string changes (React Compiler-safe
  // alternative to the synchronous setMatches inside the effect). Preserves the
  // SSR guard so it is safe on the server.
  if (prevQuery !== query) {
    setPrevQuery(query);
    if (typeof window !== 'undefined') setMatches(window.matchMedia(query).matches);
  }

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}
